<script setup>
import Layout from "../../../../layouts/main.vue";
import PageHeader from "@/components/page-header";
import { useStore } from 'vuex';
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import '@jobinsjp/vue3-datatable/dist/style.css';
import { DataTable, TableBody, TableHead, TableBodyCell } from '@jobinsjp/vue3-datatable';
import moment from 'moment';
import NotFound404 from '@/views/auth/errors/404-basic';
import Swal from 'sweetalert2';

const route = useRoute();
const store = useStore();

const title = ref("View User");
const user = computed(() => store.state.user.user);
const notification = computed(() => store.state.notification);
const loading = computed(() => store.state.globals.loading);
const items = ref([
    {
        text: "Home",
        href: "/"
    },
    {
        text: "User Management",
        href: "/user-management/users/list"
    },
    {
        text: "View User",
        active: true,
    }
]);

function getUserRoles(roles) {
    if (roles && roles.length > 0) {
      const rolesArray = roles.map(role => role.name);
      return rolesArray.join(', ');
    }
    return 'N/A';
}

function getUserLGUs(lgus) {
    if (lgus && lgus.length > 0) {
        const lgusArray = lgus.map(lgu => lgu.name);
        return lgusArray.join(', ');
    }
    return 'N/A';
}

  const deleteUser = () => {
      Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#34c38f",
        cancelButtonColor: "#f46a6a",
        confirmButtonText: "Delete",
      }).then(async (result) => {
        if (result.value) {
          await store.dispatch('user/deleteUser', { id: user.value.id, name: user.value.name });
        }
      });
  }

onMounted(() => {
    store.dispatch('user/getUser', route.params.id);
})
</script>

<template>
  <Layout>
    <PageHeader back="/user-management/users/list" :title="title" :items="items" />
    <NotFound404 v-show="loading && !user.id && notification.message" />
    <b-overlay :show="loading">
        <div v-show="user.id">
            <div class="position-relative mx-n4 mt-n4">
            <div class="profile-wid-bg profile-setting-img">
                <img
                src="@/assets/images/auth-one-bg.jpg"
                class="profile-wid-img"
                alt=""
                />
                <div class="overlay-content">
                <div class="text-end p-3">
                    <div class="p-0 ms-auto rounded-circle profile-photo-edit" />
                </div>
                </div>
            </div>
            </div>

            <div class="row">
            <div class="col-xxl-3">
                <div class="card mt-n5">
                <div class="card-body p-4">
                    <div class="text-center">
                        <img
                            v-if="user.image"
                            :src="user.image"
                            class="
                                rounded-circle
                                avatar-xl
                                img-thumbnail
                                shadow
                                mb-2
                            "
                            alt="user-profile-image"
                        />
                        <img
                            v-else
                            src="@/assets/images/users/user-dummy-img.jpg"
                            class="
                                rounded-circle
                                avatar-xl
                                img-thumbnail
                                shadow
                                mb-2
                            "
                            alt="user-profile-image"
                        />
                    <h5 class="fs-16 mb-1">{{ user.name }}</h5>
                    <p class="text-muted mb-0">{{ getUserRoles(user.roles) }}</p>
                    </div>
                </div>
                </div>
                <!--end card-->
                <div class="card">
                    <div class="card-body">
                        <div class="d-flex justify-content-between">
                            <h5 class="card-title mb-3">Info</h5>
                            <router-link :to="`/user-management/users/${user.id}/edit`">
                                <i class="mdi mdi-pencil"></i>
                                Edit User
                            </router-link>
                        </div>
                        <div class="table-responsive">
                            <table class="table table-borderless mb-0">
                                <tbody>
                                    <tr>
                                        <th class="ps-0" scope="row">Full Name</th>
                                        <td class="text-muted">{{ user.name || 'N/A' }}</td>
                                    </tr>
                                    <tr>
                                        <th class="ps-0" scope="row">Phone Number</th>
                                        <td class="text-muted">{{ user.phone_number || 'N/A' }}</td>
                                    </tr>
                                    <tr>
                                        <th class="ps-0" scope="row">E-mail Address</th>
                                        <td class="text-muted">{{ user.email || 'N/A' }} </td>
                                    </tr>
                                    <tr>
                                        <th class="ps-0" scope="row">Created Date</th>
                                        <td class="text-muted">{{ moment(user.created_at).format('YYYY-MM-DD hh:mm A') }}</td>
                                    </tr>
                                    <tr>
                                        <th class="ps-0" scope="row">Status</th>
                                        <td class="text-muted">
                                            <b-badge :variant="user.is_active ? 'success' : 'danger'">
                                                {{ user.is_active ? 'Active' : 'Inactive' }}
                                            </b-badge
                                        ></td>
                                    </tr>
                                    <tr>
                                        <th class="ps-0" scope="row">LGU</th>
                                        <td class="text-muted">{{ getUserLGUs(user.lgus) }}</td>
                                    </tr>
                                    <tr>
                                        <th class="ps-0" scope="row">Role</th>
                                        <td class="text-muted">{{ getUserRoles(user.roles) }}</td>
                                    </tr>
                                    <tr>
                                        <th class="ps-0" scope="row">e-Signature</th>
                                        <td class="text-muted">
                                             <img
                                                v-if="user.signature"
                                                :src="user.signature"
                                                class="
                                                    rounded-circle
                                                    avatar-xl
                                                    img-thumbnail
                                                    shadow
                                                    mb-2
                                                "
                                                alt="user-signature-image"
                                            />
                                            <span v-else>N/A</span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <!-- end card body -->
                </div>
            </div>
            <!--end col-->
            <div class="col-xxl-9">
                <div class="card mt-xxl-n5">
                    <div class="card-body">
                        <b-tabs content-class="mt-3" justified>
                            <b-tab title="System Logs" active>
                                <div class="row">
                                    <div class="col lg-12">
                                        <data-table :rows="[]"
                                            striped
                                            filter
                                            hoverable
                                            sortable
                                            @loadData="[]"
                                        >
                                            <template #thead>
                                                <table-head>Module</table-head>
                                                <table-head>Activity</table-head>
                                                <table-head>Date</table-head>
                                                <table-head>Action</table-head>
                                            </template>

                                            <template #tbody>
                                                <table-body>Module</table-body>
                                                <table-body>Activity</table-body>
                                                <table-body>Date</table-body>
                                                <table-body>Action</table-body>
                                            </template>

                                            <template #empty>
                                                <TableBodyCell colspan="4">
                                                    No system logs found.
                                                </TableBodyCell>
                                            </template>
                                        </data-table>
                                    </div>
                                </div>
                            </b-tab>
                            <b-tab title="Settings">
                                <div class="mb-4 pb-2">
                                    <h5 class="card-title text-decoration-underline mb-3">
                                        Settings:
                                    </h5>
                                    <div class="d-flex flex-column flex-sm-row mb-4 mb-sm-0">
                                        <div class="flex-grow-1">
                                        <h6 class="fs-14 mb-1">Two-factor Authentication</h6>
                                        <p class="text-muted">
                                            Two-factor authentication is an enhanced security
                                            measure. Once enabled, you'll be required to give two
                                            types of identification when you log into Google
                                            Authenticator.
                                        </p>
                                        </div>
                                        <div class="flex-shrink-0 ms-sm-3">
                                        <a
                                            href="javascript:void(0);"
                                            class="btn btn-sm btn-primary"
                                            >Enable Two-factor Authentication</a
                                        >
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <h5 class="card-title text-decoration-underline mb-3">
                                        Delete This Account:
                                    </h5>
                                    <p class="text-muted">
                                        Deleted accounts cannot be retrieved anymore from the system.
                                    </p>
                                    <div class="hstack gap-2 mt-3">
                                        <button @click="deleteUser" class="btn btn-soft-danger"
                                        >Close & Delete This Account</button
                                        >
                                    </div>
                                </div>
                            </b-tab>
                        </b-tabs>
                    </div>

                </div>
            </div>
            <!--end col-->
            </div>
            <!--end row-->
        </div>
    </b-overlay>
  </Layout>
</template>
